import { uniq } from 'es-toolkit';
import { BoxGeometry, BufferGeometry, EdgesGeometry, ExtrudeGeometry, Shape } from 'three';

import { PLATE_TYPES, PlateGeometryType, PlatePair, PlateType } from '@paragon/contract-library';
import {
  bevelPlateLeftHandedSuffix,
  bevelPlatePrefix,
  bevelPlateRightHandedSuffix,
  by,
  getComboPlateType,
  getPlateTypeAndSizeString,
  tPlatePrefix,
} from '@paragon/material-lists';
import { buildPlateShape, buildPlateShapeWithSlots, getPlateThicknessForVisibility } from '@paragon/three-shared';

// These are some fundamental assumptions that are made to keep this cache fast.  If any of these assumptions ever
//   change, you might need to reconsider the design of this cache.
//
//   1. A plate pair is just two instances of the same plate, so their geometries will be identical.  Thus, we don't
//        really ever need to worry about pairs, as both plates in a pair share the same geometry.
//
//   2. The only properties that are considered when constructing a plate geometry is its width, length, thickness, and
//        geometry anomalies (e.g. if it is a T plate) - together known as the shape of the plate, and identified by
//        shape keys.  Thus, we only need to calculate geometries for each unique shape rather than for each individual
//        plate pair in a component.
//
//   2. There are a very limited number of plate shapes, so once geometry has been calculated for one type of shape it
//        can be cached indefinitely.  Thus, even when the active component changes we don't ever delete
//        already-calculated plate shape geometries in case they're needed again.

interface PlatePairGeometries {
  readonly normalGeometry: BufferGeometry;
  readonly edgesGeometry: EdgesGeometry;
  readonly boundsGeometry: BufferGeometry;
}

const plateGeometriesCache = new Map<string, PlatePairGeometries>();

function buildPlateGeometry(plateShape: Shape | Shape[], thickness: number) {
  return new ExtrudeGeometry(plateShape, {
    steps: 1,
    depth: thickness,
    bevelEnabled: false,
  });
}

function buildPlateEdgesGeometry(plateShape: Shape | Shape[], thickness: number) {
  return new EdgesGeometry(buildPlateGeometry(plateShape, thickness));
}

function buildPlateBoundsGeometry(
  length: number,
  width: number,
  thickness: number,
  geometryType: PlateGeometryType,
  shape: Shape | Shape[],
) {
  if (geometryType !== 'Rectangular') {
    const boundGeometry = new ExtrudeGeometry(shape, { depth: thickness });
    return boundGeometry;
  }

  const boundGeometry = new BoxGeometry(length, width, thickness);
  boundGeometry.translate(length / 2, width / 2, thickness / 2);
  return boundGeometry;
}

export function getShapeKey(platePair: PlatePair) {
  const hingeAngleString = platePair.hingeAngle !== null ? ` ${platePair.hingeAngle}` : '';
  return `${getPlateTypeAndSizeString(platePair, true)} ${getPlateThicknessForVisibility(platePair.platePlacement)}${hingeAngleString}`;
}

function removePrefixesAndSuffixesFromPlateSizeString(plateSize: string): string {
  return plateSize
    .replace(tPlatePrefix, '')
    .replace(bevelPlatePrefix, '')
    .replace(bevelPlateRightHandedSuffix, '')
    .replace(bevelPlateLeftHandedSuffix, '');
}

export function decomposeShapeKey(shapeKey: string): {
  /**
   * Matches result of getPlateTypeString
   */
  readonly getPlateTypeStringResult: string;
  readonly plateType: PlateType;
  readonly width: number;
  readonly length: number;
  readonly geometryType: PlateGeometryType;
  readonly hingeAngle: number | null;
} {
  const [getPlateTypeStringResult, plateSize, hingeAngleString] = shapeKey.split(' ');
  let plateType: PlateType;
  if (getPlateTypeStringResult.includes('/')) {
    const plateTypeStrings = getPlateTypeStringResult.split('/');
    const comboPlateTypes = uniq(
      plateTypeStrings.map((plateTypeString) =>
        getComboPlateType(PLATE_TYPES.find((type) => type === plateTypeString)!),
      ),
    );
    if (comboPlateTypes.length !== 1) {
      throw new Error(
        'Expected exactly one unique plate type in combo plate type string, but found ' + comboPlateTypes.length,
      );
    }
    plateType = comboPlateTypes[0]!;
  } else {
    plateType = PLATE_TYPES.find((type) => type === getPlateTypeStringResult)!;
  }
  const [width, length] = removePrefixesAndSuffixesFromPlateSizeString(plateSize).split(by).map(parseFloat);
  let geometryType: PlateGeometryType = 'Rectangular';
  if (plateSize.startsWith(tPlatePrefix)) {
    geometryType = 'TPlate';
  } else if (plateSize.startsWith(bevelPlatePrefix)) {
    if (plateSize.endsWith(bevelPlateRightHandedSuffix)) {
      geometryType = 'BevelPlateRightHanded';
    } else if (plateSize.endsWith(bevelPlateLeftHandedSuffix)) {
      geometryType = 'BevelPlateLeftHanded';
    } else {
      throw new Error('Invalid bevel plate size string: missing handedness suffix');
    }
  }

  const hingeAngle = hingeAngleString ? parseFloat(hingeAngleString) : null;

  return {
    getPlateTypeStringResult,
    plateType,
    width,
    length,
    geometryType,
    hingeAngle,
  };
}

function getPlatePairGeometries(platePair: PlatePair) {
  const shapeKey = getShapeKey(platePair);

  if (!plateGeometriesCache.has(shapeKey)) {
    const shape = buildPlateShape(platePair, { slots: false });
    const shapeWithHoles = buildPlateShapeWithSlots(platePair, shape);
    const thickness = getPlateThicknessForVisibility(platePair.platePlacement);

    plateGeometriesCache.set(shapeKey, {
      normalGeometry: buildPlateGeometry(shapeWithHoles, thickness),
      edgesGeometry: buildPlateEdgesGeometry(shape, thickness),
      boundsGeometry: buildPlateBoundsGeometry(
        platePair.length,
        platePair.width,
        thickness,
        platePair.geometryType,
        shape,
      ),
    });
  }

  return plateGeometriesCache.get(shapeKey)!;
}

export function getPlatePairGeometry(platePair: PlatePair) {
  return getPlatePairGeometries(platePair).normalGeometry;
}

export function getPlatePairEdgesGeometry(platePair: PlatePair) {
  return getPlatePairGeometries(platePair).edgesGeometry;
}

export function getPlatePairBoundsGeometry(platePair: PlatePair) {
  return getPlatePairGeometries(platePair).boundsGeometry;
}
